/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { MiddlewareSteps, TransportMiddlewareProps } from '../../../../core';

import type { StreamProgressData } from '../../../../services/file-transport/interfaces';
import type { NextFunction, TransportMiddlewareContext, TransportMiddlewareRequest, TransportMiddlewareResponse } from '../../../../core';

export const handleStreamUploadResponseMiddleware = <T, R, E>(
    next: NextFunction<TransportMiddlewareResponse<R, E> | Error>,
    ctx: TransportMiddlewareContext,
    req: TransportMiddlewareRequest<T>,
    res: TransportMiddlewareResponse<R, E>
) => {
    const { streamData } = req;
    const { uploadFilesData, fileNumber, totalFiles, chunkProgress } = streamData as StreamProgressData;
    const fileData = uploadFilesData[fileNumber - 1];
    const { response } = res;

    if (!res[TransportMiddlewareProps.ResponseProp]) {
        res[TransportMiddlewareProps.ResponseProp] = [] as unknown as R;
    }

    if (!response.ok) {
        next(res);
        return;
    }

    if (Array.isArray(fileData)) {
        // chunks way
        const { fileId } = fileData[0];
        const { chunkNumber, totalChunks } = chunkProgress[fileId];
        const { chunkSize } = fileData[chunkNumber - 1];

        streamData.uploadedFilesSize += chunkSize;

        if (chunkNumber < totalChunks) {
            chunkProgress[fileId].chunkNumber += 1;
            (res[TransportMiddlewareProps.ResponseProp] as unknown as boolean[]).push(true);

            req.rerunConfig.setStep(MiddlewareSteps.Run);
            next(res);
            return;
        } else if (fileNumber < totalFiles) {
            streamData.fileNumber += 1;
            streamData.currentPieceSendAttempts = 1;
            (res[TransportMiddlewareProps.ResponseProp] as unknown as boolean[]).push(true);

            req.rerunConfig.setStep(MiddlewareSteps.Run);
            next(res);
            return;
        } else {
            (res[TransportMiddlewareProps.ResponseProp] as unknown as boolean[]).push(true);
        }
    } else {
        // full file way
        streamData.uploadedFilesSize += fileData.fileSize;

        if (fileNumber < totalFiles) {
            streamData.fileNumber += 1;
            streamData.currentPieceSendAttempts = 1;
            (res[TransportMiddlewareProps.ResponseProp] as unknown as boolean[]).push(true);

            req.rerunConfig.setStep(MiddlewareSteps.Run);
            next(res);
            return;
        } else {
            (res[TransportMiddlewareProps.ResponseProp] as unknown as boolean[]).push(true);
        }
    }

    if (res[TransportMiddlewareProps.ErrorResponse]?.length > 0) {
        // when multiple files, last file will finish here, but first file may have error
        next(res);
    } else {
        next();
    }
};
